import { useState } from 'react';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { KeyCutter } from '@mews-commander/data-access';
import { useFetchKeyCuttersQuery } from '../../queries/useFetchKeyCuttersQuery';
import preferences from '../../../preferences';
import { cut } from '../../actions';
import { FormValues } from './types';
import { KeyCutterButtonView } from './KeyCutterButtonView';
import { areSomeKeyCuttersUsable, isKeyCutterUsable } from './availability';

interface KeyCutterButtonProps {
    reservationIds: string[];
    initKeyCount?: number;
    isCheckedIn: boolean;
    textKey?: Mews.TextKey;
}

const {
    actions: { setPreferredKeyCutterId },
    selectors: { getPreferredKeyCutterId },
} = preferences;

const useGetDefaultKeyCutterId = ({ keyCutters, isCheckedIn }: { keyCutters: KeyCutter[]; isCheckedIn: boolean }) => {
    const preferredKeyCutterId = useCommanderSelector(getPreferredKeyCutterId);

    if (preferredKeyCutterId == null) {
        return null;
    }

    const keyCutterUsable = isKeyCutterUsable({
        keyCutterId: preferredKeyCutterId,
        keyCutters,
        isCheckedIn,
    });

    return keyCutterUsable ? preferredKeyCutterId : null;
};

export const KeyCutterButton = ({ isCheckedIn, reservationIds, initKeyCount, textKey }: KeyCutterButtonProps) => {
    const [activeDialog, setActiveDialog] = useState<boolean>(false);

    const { data: keyCutters } = useFetchKeyCuttersQuery();

    const handleClick = () => setActiveDialog(true);
    const handleClose = () => setActiveDialog(false);

    const dispatch = useCommanderThunkDispatch();

    const handleSubmit = async ({ keyCutter, keyCount }: FormValues) => {
        await dispatch(cut(keyCutter, reservationIds, keyCount));
        dispatch(setPreferredKeyCutterId(keyCutter));
        handleClose();
    };

    const defaultKeyCutterId = useGetDefaultKeyCutterId({ keyCutters: keyCutters ?? [], isCheckedIn });

    const someKeyCuttersUsable = areSomeKeyCuttersUsable({ keyCutters: keyCutters ?? [], isCheckedIn });

    if (!someKeyCuttersUsable) return null;

    return (
        <KeyCutterButtonView
            activeDialog={activeDialog}
            defaultKeyCutterId={defaultKeyCutterId}
            initKeyCount={initKeyCount}
            isCheckedIn={isCheckedIn}
            keyCutters={keyCutters}
            onClick={handleClick}
            onCloseDialog={handleClose}
            onSubmit={handleSubmit}
            textKey={textKey}
        />
    );
};
